import { Link } from 'react-router-dom';
import { useState, useEffect, FormEvent } from 'react';
import Button from '../components/common/Button';
import ProjectModal from '../components/ProjectModal';
import SEO from '../components/SEO';
import { contactService } from '../services/contactService';
import { testimonialService, type PublicTestimonial } from '../services/testimonialService';
import { portfolioService, type PublicProject } from '../services/portfolioService';

const HomePage = () => {
  const [selectedProject, setSelectedProject] = useState<PublicProject | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [testimonials, setTestimonials] = useState<PublicTestimonial[]>([]);
  const [portfolioProjects, setPortfolioProjects] = useState<PublicProject[]>([]);

  useEffect(() => {
    testimonialService.getAll().then(setTestimonials);
    portfolioService.getAll().then(setPortfolioProjects);
  }, []);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error' | null;
    message: string;
  }>({ type: null, message: '' });

  const openModal = (project: PublicProject) => {
    setSelectedProject(project);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setTimeout(() => setSelectedProject(null), 300);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFormSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: '' });

    try {
      // Transform data to match backend API
      const response = await contactService.submitContact({
        name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        company: formData.service, // Using service field as company
        message: `Service of Interest: ${formData.service}\n\n${formData.message}`,
      });

      if (response.success) {
        setSubmitStatus({
          type: 'success',
          message: 'Thank you! Your message has been sent successfully. We\'ll get back to you soon.',
        });
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      }
    } catch (error: any) {
      setSubmitStatus({
        type: 'error',
        message: error.response?.data?.error || 'Sorry, there was an error sending your message. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <SEO
        title="Kommonview - A Technology Company"
        description="We are a cloud-first technology company that designs, builds, and operates modern digital infrastructure at scale. Multi-cloud excellence (AWS, Azure, GCP), secure enterprise platforms, and Atlassian-powered collaboration."
        keywords="cloud infrastructure, AWS, Azure, GCP, multi-cloud solutions, DevOps, Atlassian, Jira, Confluence, IT infrastructure, digital transformation, enterprise platforms, software development, cloud migration, South Africa"
        url="https://kommonview.com/"
      />

      {/* Hero Section */}
      <section className="gradient-primary text-white py-24 lg:py-32 relative overflow-hidden">
        {/* Background dot pattern - matching live site */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div data-aos="fade-right">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-2 text-white">
                KOMMONVIEW
              </h1>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight mb-6 text-white">
                A TECHNOLOGY COMPANY.
              </h2>
              <p className="text-lg sm:text-xl text-white/90 leading-relaxed mb-4">
                We are a cloud-first technology company that designs, builds, and operates modern digital infrastructure at scale.
              </p>
              <p className="text-lg sm:text-xl text-white/80 leading-relaxed mb-8">
                As your strategic partner, we deliver multi-cloud excellence (AWS, Azure, GCP), secure enterprise platforms, Atlassian-powered collaboration, bespoke software solutions, and high-impact digital advertising, all engineered to strengthen security, accelerate innovation, and drive measurable business outcomes.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#contact" className="inline-flex items-center px-8 py-4 bg-white text-primary hover:bg-gray-100 rounded-lg font-semibold text-lg transition-all duration-200 shadow-xl hover:shadow-2xl hover:-translate-y-1">
                  Get Started
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </a>
                <a href="#portfolio" className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary rounded-lg font-semibold text-lg transition-all duration-200">
                  View Portfolio
                </a>
              </div>
            </div>
            <div className="hidden lg:block" data-aos="fade-left">
              <div className="relative">
                <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full"></div>
                <div className="relative bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-accent rounded-lg flex items-center justify-center">
                        <i className="fas fa-cloud text-white text-xl"></i>
                      </div>
                      <div>
                        <div className="text-white font-semibold">Multi-Cloud Excellence</div>
                        <div className="text-white/70 text-sm">AWS, Azure, GCP</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-secondary rounded-lg flex items-center justify-center">
                        <i className="fas fa-cogs text-white text-xl"></i>
                      </div>
                      <div>
                        <div className="text-white font-semibold">DevOps Implementation</div>
                        <div className="text-white/70 text-sm">CI/CD & Automation</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-primary rounded-lg flex items-center justify-center">
                        <i className="fab fa-atlassian text-white text-xl"></i>
                      </div>
                      <div>
                        <div className="text-white font-semibold">Atlassian Services</div>
                        <div className="text-white/70 text-sm">Jira, Confluence, Opsgenie</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trusted By */}
      <section className="py-12 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 text-sm mb-6 uppercase tracking-wide">Trusted By</p>
          <div className="relative">
            <style>{`
              @keyframes scroll {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              .animate-scroll {
                animation: scroll 30s linear infinite;
              }
              .logo-container:hover .animate-scroll {
                animation-play-state: paused;
              }
            `}</style>
            <div className="logo-container flex items-center">
              <div className="animate-scroll flex items-center gap-16 px-8">
                {/* First set of logos */}
                <a href="https://fptacademy.co.za" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/fpt-academy.jpg" alt="FPT Academy" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                <a href="https://elelwani.com" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/elelwani.png" alt="Elelwani Catering Services" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                <a href="https://sambeconsulting.com" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/sambe-consulting.png" alt="Sambe Consulting" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                <div className="flex-shrink-0">
                  <img src="/images/logos/ovasyt.png" alt="Ovasyt (Currently Offline)" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </div>
                <a href="https://akademus.io" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/akademus.png" alt="Akademus" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                {/* Duplicate set for seamless loop */}
                <a href="https://fptacademy.co.za" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/fpt-academy.jpg" alt="FPT Academy" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                <a href="https://elelwani.com" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/elelwani.png" alt="Elelwani Catering Services" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                <a href="https://sambeconsulting.com" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/sambe-consulting.png" alt="Sambe Consulting" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
                <div className="flex-shrink-0">
                  <img src="/images/logos/ovasyt.png" alt="Ovasyt (Currently Offline)" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </div>
                <a href="https://akademus.io" target="_blank" rel="noopener noreferrer" className="flex-shrink-0">
                  <img src="/images/logos/akademus.png" alt="Akademus" className="h-12 object-contain rounded-lg opacity-60 grayscale hover:opacity-100 hover:grayscale-0 transition-all" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center" data-aos="fade-up" data-aos-delay="0">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">5+</div>
              <div className="text-gray-600 font-medium">Platforms Built</div>
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="100">
              <div className="text-4xl md:text-5xl font-bold text-secondary mb-2">100%</div>
              <div className="text-gray-600 font-medium">Client Satisfaction</div>
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="200">
              <div className="text-4xl md:text-5xl font-bold text-accent mb-2">3</div>
              <div className="text-gray-600 font-medium">Cloud Providers</div>
            </div>
            <div className="text-center" data-aos="fade-up" data-aos-delay="300">
              <div className="text-4xl md:text-5xl font-bold text-primary mb-2">10+</div>
              <div className="text-gray-600 font-medium">Years Experience</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Comprehensive technology solutions tailored to your business needs
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* IT Infrastructure */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-primary group hover:-translate-y-2" data-aos="fade-up" data-aos-delay="0">
              <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-server text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-primary transition-colors">IT Infrastructure Services</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Hardware & Software</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Server Compute Support (Windows/Linux)</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Client Compute Support (Windows/Linux/macOS)</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Networking</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Hardware Repairs (Server & Client)</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Web App Design & Development</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Desktop Application Development</span>
                </li>
              </ul>
            </div>

            {/* Cloud & SaaS */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-secondary group hover:-translate-y-2" data-aos="fade-up" data-aos-delay="100">
              <div className="w-16 h-16 bg-secondary rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fas fa-cloud text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-secondary transition-colors">Public Cloud & SaaS Platforms</h3>
              <ul className="space-y-3 text-gray-700 mb-6">
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Cloud Infrastructure Management (AWS, Azure, GCP)</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Hybrid Cloud Integrations</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Microsoft 365 Setup & Management</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Google Workspace Setup & Management</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Elasticsearch Support</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Datadog Support</span>
                </li>
              </ul>
            </div>

            {/* Atlassian */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-accent group hover:-translate-y-2" data-aos="fade-up" data-aos-delay="200">
              <div className="w-16 h-16 bg-accent rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className="fab fa-atlassian text-3xl text-white"></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 group-hover:text-accent transition-colors">Atlassian Cloud Services</h3>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Jira Service Management</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Jira Software Implementation</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Confluence</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Opsgenie</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Atlassian Product Automation</span>
                </li>
                <li className="flex items-start">
                  <i className="fas fa-check-circle text-primary mt-1 mr-3 flex-shrink-0"></i>
                  <span>Atlassian Product Integration</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/services">
              <Button variant="primary" size="lg">View All Services</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16" data-aos="fade-up">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Our Portfolio</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Explore our successful projects and implementations
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolioProjects.map((project, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="bg-white h-56 flex items-center justify-center p-6 border-b border-gray-100">
                  <img
                    src={project.logo}
                    alt={`${project.title} logo`}
                    className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-300 rounded-2xl"
                    loading="lazy"
                  />
                </div>
                <div className="p-6">
                  <div className="text-sm text-accent font-semibold mb-2 uppercase tracking-wide">
                    {project.category}
                  </div>
                  <h3 className="text-xl font-heading font-bold mb-2 text-gray-900 group-hover:text-primary transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{project.description}</p>

                  {/* Action Buttons */}
                  <div className="flex gap-3">
                    <button
                      onClick={() => openModal(project)}
                      className="flex-1 flex items-center justify-center text-primary font-medium hover:text-primary-700 transition-colors text-sm"
                    >
                      <span>Read More</span>
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    {project.url && (
                      <a
                        href={project.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center text-primary font-medium hover:text-primary-700 transition-colors text-sm"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span>Visit Site</span>
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12" data-aos="fade-up">
            <Link to="/portfolio">
              <Button variant="primary" size="lg">View Full Portfolio</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Project Modal */}
      {selectedProject && (
        <ProjectModal
          isOpen={isModalOpen}
          onClose={closeModal}
          project={selectedProject}
        />
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section id="testimonials" className="py-20 bg-gray-50 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16" data-aos="fade-up">
              <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">Client Testimonials</h2>
              <p className="text-xl text-gray-600">Hear what our clients have to say about working with Kommonview</p>
            </div>

            <div className="relative">
              <style>{`
                @keyframes scrollTestimonials {
                  0% { transform: translateX(0); }
                  100% { transform: translateX(-50%); }
                }
                .animate-scroll-testimonials {
                  animation: scrollTestimonials 40s linear infinite;
                }
                .testimonial-container:hover .animate-scroll-testimonials {
                  animation-play-state: paused;
                }
              `}</style>
              <div className="testimonial-container flex items-center">
                {/* Duplicate the list for seamless infinite scroll */}
                <div className="animate-scroll-testimonials flex items-stretch gap-8 px-4">
                  {[...testimonials, ...testimonials].map((t, i) => {
                    const colors = ['primary', 'secondary', 'accent'];
                    const color = colors[i % colors.length];
                    return (
                      <div key={`${t.id}-${i}`} className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-shadow flex-shrink-0 w-96">
                        <div className="flex items-center mb-6">
                          {t.avatar ? (
                            <img src={t.avatar} alt={t.name} className="w-14 h-14 rounded-full object-cover mr-4" />
                          ) : (
                            <div className={`w-14 h-14 bg-${color}/10 rounded-full flex items-center justify-center mr-4`}>
                              <i className={`fas fa-user text-${color} text-2xl`}></i>
                            </div>
                          )}
                          <div>
                            <h4 className="font-bold text-gray-900 text-lg">{t.name}</h4>
                            <p className="text-sm text-gray-600">
                              {t.role}{t.company ? `, ${t.company}` : ''}
                            </p>
                          </div>
                        </div>
                        <div className="mb-4">
                          <i className="fas fa-quote-left text-accent text-2xl opacity-30"></i>
                        </div>
                        <p className="text-gray-700 leading-relaxed italic">{t.content}</p>
                        <div className="mt-4 flex">
                          {Array.from({ length: t.rating }).map((_, s) => (
                            <i key={s} className="fas fa-star text-accent"></i>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="gradient-primary text-white py-20 relative overflow-hidden">
        {/* Background dot pattern - matching live site */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-aos="fade-up">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">Ready to Transform Your Business?</h2>
          <p className="text-xl mb-8 text-white/90">Let's build something exceptional together. Get started with a free consultation.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="#contact" className="inline-flex items-center px-8 py-4 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all shadow-2xl hover:shadow-xl hover:-translate-y-1">
              Get in Touch
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <a href="#services" className="inline-flex items-center px-8 py-4 bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary font-semibold rounded-lg transition-all">
              Explore Services
            </a>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12" data-aos="fade-up">
            <div className="text-center mb-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Invitation</h2>
              <p className="text-lg text-gray-600">The house of kommonview can't wait to be of service to you.</p>
            </div>

            <form onSubmit={handleFormSubmit}>
              {submitStatus.type && (
                <div className={`mb-4 p-3 rounded ${submitStatus.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' : 'bg-red-50 border border-red-200 text-red-800'}`}>
                  <i className={`fas ${submitStatus.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-triangle'} mr-2`}></i>
                  {submitStatus.message}
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label htmlFor="fullName" className="block text-xs font-medium text-gray-700 mb-1">Full Name *</label>
                  <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleFormChange} required autoComplete="name" placeholder="e.g. Jane Doe" className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-transparent transition-all" />
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1">Email Address *</label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleFormChange} required autoComplete="email" placeholder="you@company.com" className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-transparent transition-all" />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-xs font-medium text-gray-700 mb-1">Phone Number</label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleFormChange} autoComplete="tel" placeholder="+27 XX XXX XXXX" className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-transparent transition-all" />
                </div>

                <div>
                  <label htmlFor="service" className="block text-xs font-medium text-gray-700 mb-1">Service of Interest *</label>
                  <select id="service" name="service" value={formData.service} onChange={handleFormChange} required className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-transparent transition-all">
                    <option value="" disabled>Select a service</option>
                    <option value="IT Infrastructure Services">IT Infrastructure Services</option>
                    <option value="Public Cloud & SaaS Platforms">Public Cloud & SaaS Platforms</option>
                    <option value="DevOps Implementation">DevOps Implementation</option>
                    <option value="Atlassian Cloud Services">Atlassian Cloud Services</option>
                    <option value="Digital Advertising Solutions">Digital Advertising Solutions</option>
                    <option value="Multiple Services">Contact Me - I Need Multiple Services</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="message" className="block text-xs font-medium text-gray-700 mb-1">Message *</label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleFormChange} rows={2} required placeholder="Tell us about your project or request" className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-primary focus:border-transparent transition-all resize-none"></textarea>
                </div>

                <div className="md:col-span-2">
                  <button type="submit" disabled={isSubmitting} className="w-full py-2 gradient-primary text-white rounded font-semibold text-sm transition-all duration-200 shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed">
                    {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
